import type {
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { faceOrNullMoveAlongNormal, faceOrNullCopy } from "geom/brep";
import { extrudeLine2 } from "geom/extrude";
import { planeNewXY } from "geom/plane";
import {
  polyLine2Copy,
  polyLine2ExtendStart,
  polyLine2Length,
  polyLine2ExtendEnd,
  polyLine2ReverseCopy,
} from "geom/polyLine2";
import { getEaveDepth } from "../../../elements/roofTrimUtility";

/**
 * Generates some special faces for shed roofs. Those faces are used for splitting
 * a regular gable roof into a shed roof. Depending on the shed roof type (left/right),
 * the faces are generated pointing in different directions.
 */
export function generateRoofVolumeHalfFaces({
  roofVolume,
  roofVolumeOptions,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const {
    baseHeight,
    baseToPeak,
    roofType,
    rails2D,
    rectangularOverhangs,
    isUnboundedShedRoof,
    verticalRails,
    startConnectivity,
    endConnectivity,
  } = roofVolume;

  const { roofInputSettings } = roofVolumeOptions;
  const { roofSettings } = roofInputSettings;

  let shedRoofHalfFace: Face | null = null;
  let shedRoofWallHalfFace: Face | null = null;

  // early return for non-shed roofs
  if (roofType === GableRoofType.DEFAULT) {
    return {
      roofVolume: {
        shedRoofHalfFace,
        shedRoofWallHalfFace,
      },
    };
  }

  const { baseStartOverhang, baseEndOverhang, baseLeftOverhang, baseRightOverhang } = rectangularOverhangs;

  const { isFree: isStartFree } = startConnectivity;
  const { isFree: isEndFree } = endConnectivity;
  const { peakRail, peakRailNoExtension } = rails2D;
  const { isStartTruncated, isEndTruncated } = verticalRails;

  // build the half edge
  const hasTruncation = isStartTruncated || isEndTruncated;

  let halfFaceEdge = polyLine2Copy(hasTruncation ? peakRail : peakRailNoExtension);

  if (isStartFree && !isStartTruncated) {
    polyLine2ExtendStart(
      halfFaceEdge,
      -Math.min(baseStartOverhang, polyLine2Length(halfFaceEdge) - MULTIGABLE_BSP_EPSILON),
    );
  }

  if (isEndFree && !isEndTruncated) {
    polyLine2ExtendEnd(
      halfFaceEdge,
      -Math.min(baseEndOverhang, polyLine2Length(halfFaceEdge) - MULTIGABLE_BSP_EPSILON),
    );
  }

  // flip the edge if needed
  if (roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF) {
    halfFaceEdge = polyLine2ReverseCopy(halfFaceEdge);
  }

  // build the half face
  shedRoofHalfFace = extrudeLine2(
    planeNewXY(0, 0, baseHeight - getEaveDepth(roofSettings)),
    halfFaceEdge[0],
    halfFaceEdge[1],
    halfFaceEdge[2],
    halfFaceEdge[3],
    0,
    0,
    baseToPeak + getEaveDepth(roofSettings) - GABLE_ROOF_THICKNESS,
  );

  if (isUnboundedShedRoof) {
    // build the wall half face for unbounded shed roofs (not connected to side neighbors)
    // (we copy the shed roof half face and move it inside using the overhang)
    const highEaveOverhang = roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? baseRightOverhang : baseLeftOverhang;
    shedRoofWallHalfFace = faceOrNullMoveAlongNormal(faceOrNullCopy(shedRoofHalfFace), -highEaveOverhang);
  } else {
    // build the wall half face for side connected shed roofs
    // (it's just a copy of the shed roof half face)
    shedRoofWallHalfFace = faceOrNullCopy(shedRoofHalfFace);
  }

  return {
    roofVolume: {
      shedRoofHalfFace,
      shedRoofWallHalfFace,
    },
  };
}
